/**
 * InstrumentDetail Component
 *
 * Displays detailed information about a financial instrument.
 * Handles matched, unmatched, and ambiguous positions.
 *
 * If instrument_id is provided, fetches directly by ID.
 * Otherwise, resolves symbol to find matching instrument.
 * NOW THEME-AWARE and i18n SUPPORTED
 *
 * Duplicate Resolution:
 * When linking a position to an instrument already linked to another position,
 * shows a dialog offering to merge (add quantity) or replace existing position.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useRoute, useNavigation } from '@react-navigation/native';
import axios from 'axios';
import { getToken } from '../../services/tokenStorage';
import { logApiRequest, logApiResponse, logApiError } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { InstrumentSelector } from './InstrumentSelector';
import { PriceChart } from './PriceChart';
import { DuplicateInstrumentDialog, PositionSnapshot } from './DuplicateInstrumentDialog';
import { financialDataAPI, CompanyProfile, Quote } from '../../services/api/financialDataApi';

const API_URL = process.env.EXPO_PUBLIC_API_URL || 'https://topnote-api-sbp5f6nlfa-uc.a.run.app';

interface Instrument {
  id: number;
  symbol: string;
  company_name?: string;
  sector?: string;
  industry?: string;
  exchange?: string;
  currency: string;
  description?: string;
}

interface InstrumentCandidate {
  id: number;
  symbol: string;
  company_name?: string;
  exchange?: string;
  currency: string;
}

interface ResolutionResult {
  status: 'matched' | 'unmatched' | 'ambiguous';
  instrument_id?: number;
  normalized_symbol: string;
  candidates: InstrumentCandidate[];
  linked_trades?: number;
  linked_positions?: number;
}

interface InstrumentDetailProps {
  onLinked?: (instrumentId: number) => void;
}

export function InstrumentDetail({ onLinked }: InstrumentDetailProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const route = useRoute();
  const navigation = useNavigation();
  const { symbol, currency, instrument_id, position_id, quantity, cost_price } = route.params as {
    symbol: string;
    currency: string;
    instrument_id?: number;
    position_id?: number;
    quantity?: number;
    cost_price?: number;
  };
  const { userId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [resolution, setResolution] = useState<ResolutionResult | null>(null);
  const [instrument, setInstrument] = useState<Instrument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  // Duplicate detection state
  const [existingPosition, setExistingPosition] = useState<PositionSnapshot | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingInstrument, setPendingInstrument] = useState<InstrumentCandidate | null>(null);

  // User Story 2: Add state for company profile, quote, and historical prices
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [historicalPrices, setHistoricalPrices] = useState<any[] | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    // If instrument_id is provided, fetch directly; otherwise resolve by symbol
    if (instrument_id) {
      fetchInstrumentDetails(instrument_id);
    } else {
      setIsResolving(true);
      resolveInstrument();
    }
  }, [symbol, currency, instrument_id, userId]);

  // Auto-open selector when status is ambiguous
  useEffect(() => {
    if (resolution?.status === 'ambiguous') {
      setShowSelector(true);
    }
  }, [resolution?.status]);

  // User Story 2: Fetch quote when instrument is matched (profile is embedded in instrument response)
  const fetchQuote = useCallback(async () => {
    const token = await getToken();
    if (!token || !instrument) {
      return;
    }

    // Fetch latest quote only (profile is already in instrument response)
    setQuoteLoading(true);
    try {
      const quoteData = await financialDataAPI.getQuote(instrument.id, token);
      setQuote(quoteData);
    } catch (err: any) {
      console.error('Failed to fetch quote:', err);
      // Don't show error for quote failures - it's optional
    } finally {
      setQuoteLoading(false);
    }
  }, [instrument]); // Depend on instrument so we always have the latest value

  useEffect(() => {
    if (instrument && resolution?.status === 'matched') {
      fetchQuote();
    }
  }, [instrument, resolution?.status, fetchQuote]); // Add fetchQuote to dependencies

  // Auto-link position to instrument after resolve (no duplicate conflict)
  const autoLinkPosition = async (instrumentId: number) => {
    if (!position_id || !userId) return;

    const startTime = Date.now();
    const url = `${API_URL}/api/v1/financial-data/instruments/link-single`;
    const method = 'POST';

    try {
      const token = await getToken();
      if (!token) return;

      const requestBody = {
        instrument_id: instrumentId,
        position_id: position_id,
        user_id: userId,
      };

      logApiRequest(method, url, requestBody);

      const response = await axios.post(url, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const duration = Date.now() - startTime;
      logApiResponse(method, url, response.status, duration);

      console.log('[InstrumentDetail] Position auto-linked:', {
        instrument_id: instrumentId,
        position_id: position_id,
      });
    } catch (err: any) {
      const duration = Date.now() - startTime;
      logApiError(method, url, err, duration);
      console.error(
        '[InstrumentDetail] Auto-link failed:',
        err.response?.data?.detail || err.message
      );
      // Don't set error state - instrument details will still display
      // User can manually click the Link button as fallback
    }
  };

  const resolveInstrument = async () => {
    const startTime = Date.now();
    const url = `${API_URL}/api/v1/financial-data/instruments/resolve`;
    const method = 'POST';

    try {
      setLoading(true);
      setError(null);

      // Get authentication token
      const token = await getToken();
      if (!token) {
        logApiError(method, url, new Error('Authentication required'));
        setError('Authentication required');
        return;
      }

      // Prepare request body
      // IMPORTANT: Don't pass user_id when we have a position_id
      // This prevents auto-linking which bypasses duplicate detection
      const requestBody: any = { symbol, currency };

      // Only pass user_id for auto-linking if we're NOT linking a specific position
      // When position_id is provided, user wants to manually link and check for duplicates
      if (userId && !position_id) {
        requestBody.user_id = userId;
      }

      logApiRequest(method, url, requestBody);

      // Resolve symbol to instrument
      const response = await axios.post(url, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const duration = Date.now() - startTime;
      logApiResponse(method, url, response.status, duration);

      const result: ResolutionResult = response.data;
      setResolution(result);

      // If matched, check for duplicates before fetching instrument details
      // When we have a specific position_id, we need to check for duplicates first
      if (result.status === 'matched' && result.instrument_id) {
        if (position_id) {
          // We're linking a specific position - check for duplicates before auto-fetching
          const hasDuplicate = await checkForDuplicateInstrument(result.instrument_id);

          if (hasDuplicate) {
            // Duplicate found - set pending instrument for the dialog
            // Create a candidate object from the matched instrument
            setPendingInstrument({
              id: result.instrument_id,
              symbol: symbol,
              company_name: '', // Will be fetched when needed
              exchange: '',
              currency: currency,
            });
            // Dialog is already shown by checkForDuplicateInstrument
            // Don't fetch instrument details yet
            return;
          }

          // No duplicate - auto-link the position and fetch instrument details
          await autoLinkPosition(result.instrument_id);
          await fetchInstrumentDetails(result.instrument_id);
        } else {
          // No specific position_id - this is a general lookup, fetch details directly
          await fetchInstrumentDetails(result.instrument_id);
        }
      }
    } catch (err: any) {
      const duration = Date.now() - startTime;
      logApiError(method, url, err, duration);

      // Extract error message
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to resolve instrument';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstrumentDetails = async (instrumentId: number) => {
    const startTime = Date.now();
    const url = `${API_URL}/api/v1/financial-data/instruments/${instrumentId}`;
    const method = 'GET';

    try {
      // Get authentication token
      const token = await getToken();
      if (!token) {
        logApiError(method, url, new Error('Authentication required'));
        setError('Authentication required');
        return;
      }

      logApiRequest(method, url, { instrumentId });

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const duration = Date.now() - startTime;
      logApiResponse(method, url, response.status, duration);

      const data = response.data;

      // Set instrument basic data
      setInstrument(data);

      // Extract company profile from response (if available)
      if (data.company_profile) {
        setProfile(data.company_profile);
      }

      // Extract historical prices from response (if available)
      if (data.historical_prices && Array.isArray(data.historical_prices)) {
        setHistoricalPrices(data.historical_prices);
      }

      onLinked?.(instrumentId);
    } catch (err: any) {
      const duration = Date.now() - startTime;
      logApiError(method, url, err, duration);

      // Extract error message
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to fetch instrument details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if the selected instrument is already linked to another position
  const checkForDuplicateInstrument = async (instrumentId: number): Promise<boolean> => {
    const token = await getToken();
    if (!token || !userId) {
      return false;
    }

    try {
      // The /positions endpoint uses the authenticated user from JWT token
      const response = await axios.get(`${API_URL}/api/v1/portfolio/positions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const positions = response.data;

      // Find a position that is linked to the instrument but is not the current position
      const existing = positions.find(
        (p: any) => p.instrument_id === instrumentId && p.id !== position_id
      );

      if (existing) {
        setExistingPosition({
          id: existing.id,
          symbol: existing.symbol,
          quantity: existing.quantity,
          cost_price: existing.cost_price,
          currency: existing.currency,
        });
        setShowDuplicateDialog(true);
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('[InstrumentDetail] Failed to check for duplicate:', err);
      // If check fails, proceed without showing dialog
      return false;
    }
  };

  // Handle the duplicate resolution (add or replace)
  const handleDuplicateResolution = async (action: 'add' | 'replace') => {
    if (!pendingInstrument || !existingPosition || !userId) {
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const requestBody: any = {
        instrument_id: pendingInstrument.id,
        existing_position_id: existingPosition.id,
        new_position_symbol: symbol,
        new_position_quantity: quantity || 0,
        new_position_cost: cost_price,
        action: action,
        user_id: userId,
      };

      await axios.post(
        `${API_URL}/api/v1/financial-data/instruments/link-with-resolution`,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Close dialog and navigate back
      setShowDuplicateDialog(false);
      setPendingInstrument(null);
      setExistingPosition(null);

      // Navigate back to refresh the portfolio
      navigation.goBack();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to resolve duplicate';
      setError(errorMessage);
      setShowDuplicateDialog(false);
    }
  };

  // Handle direct link button when there's no duplicate
  const handleLinkPosition = async () => {
    if (!instrument?.id || !position_id || !userId) {
      console.error('[InstrumentDetail] Missing required data for linking:', {
        instrument_id: instrument?.id,
        position_id,
        user_id: userId,
      });
      setError('Missing required data for linking');
      return;
    }

    const startTime = Date.now();
    const url = `${API_URL}/api/v1/financial-data/instruments/link-single`;
    const method = 'POST';

    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const requestBody = {
        instrument_id: instrument.id,
        position_id: position_id,
        user_id: userId,
      };

      logApiRequest(method, url, requestBody);

      const response = await axios.post(url, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const duration = Date.now() - startTime;
      logApiResponse(method, url, response.status, duration);

      console.log('[InstrumentDetail] Position linked successfully:', {
        instrument_id: instrument.id,
        position_id: position_id,
      });

      // Navigate back to refresh the portfolio
      navigation.goBack();
    } catch (err: any) {
      const duration = Date.now() - startTime;
      logApiError(method, url, err, duration);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to link position';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCandidate = async (candidate: InstrumentCandidate) => {
    const startTime = Date.now();
    let url: string;
    let method = 'POST';
    let requestBody: any;

    try {
      setLoading(true);
      setError(null);

      // Get authentication token
      const token = await getToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      let instrumentId: number;

      if (candidate.id === 0) {
        // Candidate is from FMP search - create instrument and link
        url = `${API_URL}/api/v1/financial-data/instruments/link`;
        requestBody = {
          fmp_symbol: candidate.symbol,
          original_symbol: symbol,
          currency: currency,
          user_id: userId,
        };

        logApiRequest(method, url, requestBody);

        const response = await axios.post(url, requestBody, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const duration = Date.now() - startTime;
        logApiResponse(method, url, response.status, duration);

        instrumentId = response.data.instrument_id;
      } else {
        // Candidate is already in database - link existing instrument
        // Check for duplicate BEFORE linking
        const hasDuplicate = await checkForDuplicateInstrument(candidate.id);

        if (hasDuplicate) {
          // Store pending candidate for later use after user resolves duplicate
          setPendingInstrument(candidate);
          setLoading(false);
          return;
        }

        // No duplicate - proceed with linking
        // If we have a specific position_id, use link-single to avoid affecting other positions
        if (position_id) {
          // Link only this specific position
          url = `${API_URL}/api/v1/financial-data/instruments/link-single`;
          requestBody = {
            instrument_id: candidate.id,
            position_id: position_id,
            user_id: userId,
          };
        } else {
          // No specific position - link all positions with this symbol (legacy behavior)
          url = `${API_URL}/api/v1/financial-data/instruments/link-existing`;
          requestBody = {
            instrument_id: candidate.id,
            original_symbol: symbol,
            user_id: userId,
          };
        }

        logApiRequest(method, url, requestBody);

        const response = await axios.post(url, requestBody, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const duration = Date.now() - startTime;
        logApiResponse(method, url, response.status, duration);

        instrumentId = candidate.id;
      }

      // For FMP candidates (id === 0), also check for duplicate after creating
      if (candidate.id === 0 && instrumentId) {
        const hasDuplicate = await checkForDuplicateInstrument(instrumentId);
        if (hasDuplicate) {
          setPendingInstrument({ ...candidate, id: instrumentId });
          setLoading(false);
          return;
        }
      }

      // Fetch the instrument details after linking
      await fetchInstrumentDetails(instrumentId);

      // Update resolution state to "matched" so UI refreshes
      setResolution({
        status: 'matched',
        instrument_id: instrumentId,
        normalized_symbol: symbol,
        candidates: [],
      });

      setShowSelector(false);
    } catch (err: any) {
      const duration = Date.now() - startTime;
      logApiError(method, url || 'unknown', err, duration);
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to load candidate details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Render content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
            {isResolving
              ? t('portfolio.resolvingSymbol', { defaultValue: 'Resolving symbol...' })
              : t('portfolio.loadingInstrument', { defaultValue: 'Loading instrument...' })}
          </Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
          <View
            style={[styles.statusBox, styles.errorBox, { backgroundColor: theme.colors.surface }]}
          >
            <Text style={[styles.errorTitle, { color: theme.colors.error }]}>
              {t('common.error')}
            </Text>
            <Text style={[styles.errorMessage, { color: theme.colors.textSecondary }]}>
              {error}
            </Text>
          </View>
        </View>
      );
    }

    // Unmatched position
    if (resolution?.status === 'unmatched') {
      return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
          <View
            style={[
              styles.statusBox,
              styles.unmatchedBox,
              { backgroundColor: theme.colors.surface },
            ]}
          >
            <Text style={[styles.statusTitle, { color: theme.colors.text }]}>
              ⚠️ {t('portfolio.unmatchedPosition', { defaultValue: 'Unmatched Position' })}
            </Text>
            <Text style={[styles.statusText, { color: theme.colors.text }]}>
              {t('portfolio.symbol')}: {symbol} ({currency})
            </Text>
            <Text style={[styles.statusDetail, { color: theme.colors.textSecondary }]}>
              {t('portfolio.couldNotFindInstrument', {
                defaultValue: 'Could not find this instrument in the database.',
              })}
            </Text>
            <TouchableOpacity
              style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
              onPress={resolveInstrument}
            >
              <Text style={[styles.retryButtonText, { color: theme.colors.textInverse }]}>
                {t('common.retry')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.retryButton,
                { backgroundColor: theme.colors.primary, marginTop: theme.spacing.sm },
              ]}
              onPress={() => setShowSelector(true)}
            >
              <Text style={[styles.retryButtonText, { color: theme.colors.textInverse }]}>
                {t('portfolio.searchManually', { defaultValue: 'Search Manually' })}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    // Ambiguous position - auto-open selector
    if (resolution?.status === 'ambiguous') {
      return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
          <View
            style={[
              styles.statusBox,
              styles.ambiguousBox,
              { backgroundColor: theme.colors.surface },
            ]}
          >
            <Text style={[styles.statusTitle, { color: theme.colors.text }]}>
              ❓ {t('portfolio.ambiguousMatch', { defaultValue: 'Ambiguous Match' })}
            </Text>
            <Text style={[styles.statusText, { color: theme.colors.text }]}>
              {t('portfolio.multipleInstrumentsFound', {
                defaultValue: 'Multiple instruments found for {{symbol}}',
                symbol: symbol,
              })}
            </Text>
            <Text style={[styles.statusDetail, { color: theme.colors.textSecondary }]}>
              {t('portfolio.openingSearchSelector', { defaultValue: 'Opening search selector...' })}
            </Text>
            <TouchableOpacity
              style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
              onPress={() => setShowSelector(true)}
            >
              <Text style={[styles.retryButtonText, { color: theme.colors.textInverse }]}>
                {t('portfolio.openSearch', { defaultValue: 'Open Search' })}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    // Matched position with instrument details
    return (
      <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        {/* Basic instrument info */}
        <View style={[styles.matchedBox, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.header}>
            <Text style={[styles.symbol, { color: theme.colors.text }]}>
              {instrument?.symbol || symbol}
            </Text>
            <View style={[styles.badge, { backgroundColor: theme.colors.success }]}>
              <Text style={[styles.badgeText, { color: theme.colors.textInverse }]}>
                ✓ {t('portfolio.matched', { defaultValue: 'Matched' })}
              </Text>
            </View>
          </View>

          {instrument?.company_name && (
            <Text style={[styles.companyName, { color: theme.colors.text }]}>
              {instrument.company_name}
            </Text>
          )}

          <View style={styles.details}>
            {instrument?.sector && (
              <DetailRow label={t('portfolio.sector')} value={instrument.sector} theme={theme} />
            )}
            {instrument?.industry && (
              <DetailRow
                label={t('portfolio.industry')}
                value={instrument.industry}
                theme={theme}
              />
            )}
            {instrument?.exchange && (
              <DetailRow
                label={t('portfolio.exchange')}
                value={instrument.exchange}
                theme={theme}
              />
            )}
            <DetailRow label={t('portfolio.currency')} value={currency} theme={theme} />
          </View>

          {/* Link button - show when we have a position_id to link */}
          {position_id && instrument && (
            <TouchableOpacity
              style={[
                styles.retryButton,
                { backgroundColor: theme.colors.primary, marginTop: theme.spacing.md },
              ]}
              onPress={handleLinkPosition}
            >
              <Text style={[styles.retryButtonText, { color: theme.colors.textInverse }]}>
                {t('portfolio.linkPosition', { defaultValue: 'Link Position to Instrument' })}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* User Story 2: Latest Quote */}
        {quoteLoading && (
          <View style={[styles.sectionBox, { backgroundColor: theme.colors.surface }]}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Text style={[styles.sectionLoadingText, { color: theme.colors.textSecondary }]}>
              {t('portfolio.loadingQuote', { defaultValue: 'Loading quote...' })}
            </Text>
          </View>
        )}

        {!quoteLoading && (quote || historicalPrices) && (
          <View style={[styles.sectionBox, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {t('portfolio.latestPrice', { defaultValue: 'Latest Price' })}
            </Text>
            {/* Show quote data if available */}
            {quote && (
              <>
                <View style={styles.priceContainer}>
                  <Text style={[styles.price, { color: theme.colors.text }]}>
                    ${quote.price?.toFixed(2) || 'N/A'}
                  </Text>
                  {quote.change !== undefined && quote.change !== null && (
                    <Text
                      style={[
                        styles.priceChange,
                        quote.change >= 0
                          ? { color: theme.colors.success }
                          : { color: theme.colors.error },
                      ]}
                    >
                      {quote.change >= 0 ? '+' : ''}
                      {quote.change.toFixed(2)}({quote.changes_percentage?.toFixed(2) || 'N/A'}%)
                    </Text>
                  )}
                </View>
              </>
            )}

            {/* Show OHLCV from latest historical price (last trading day) */}
            {historicalPrices && historicalPrices.length > 0 && (
              <View style={styles.details}>
                <DetailRow
                  label={t('portfolio.date')}
                  value={historicalPrices[0].date}
                  theme={theme}
                />
                <DetailRow
                  label={t('portfolio.open')}
                  value={
                    historicalPrices[0].open !== null
                      ? `$${historicalPrices[0].open.toFixed(2)}`
                      : 'N/A'
                  }
                  theme={theme}
                />
                <DetailRow
                  label={t('portfolio.high')}
                  value={
                    historicalPrices[0].high !== null
                      ? `$${historicalPrices[0].high.toFixed(2)}`
                      : 'N/A'
                  }
                  theme={theme}
                />
                <DetailRow
                  label={t('portfolio.low')}
                  value={
                    historicalPrices[0].low !== null
                      ? `$${historicalPrices[0].low.toFixed(2)}`
                      : 'N/A'
                  }
                  theme={theme}
                />
                <DetailRow
                  label={t('portfolio.close')}
                  value={
                    historicalPrices[0].close !== null
                      ? `$${historicalPrices[0].close.toFixed(2)}`
                      : 'N/A'
                  }
                  theme={theme}
                />
                <DetailRow
                  label={t('portfolio.volume')}
                  value={
                    historicalPrices[0].volume !== null
                      ? historicalPrices[0].volume.toLocaleString()
                      : 'N/A'
                  }
                  theme={theme}
                />
              </View>
            )}

            {quote?.data_freshness?.last_update && (
              <Text style={[styles.freshnessText, { color: theme.colors.textTertiary }]}>
                {t('portfolio.updated', { defaultValue: 'Updated' })}:{' '}
                {new Date(quote.data_freshness.last_update).toLocaleString()}
              </Text>
            )}
          </View>
        )}

        {/* Show quote unavailable message if not loading and no quote or historical prices */}
        {!quoteLoading && !quote && (!historicalPrices || historicalPrices.length === 0) && (
          <View style={[styles.sectionBox, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {t('portfolio.latestPrice', { defaultValue: 'Latest Price' })}
            </Text>
            <Text style={[styles.noDataText, { color: theme.colors.textTertiary }]}>
              {t('portfolio.priceDataNotAvailable', { defaultValue: 'Price data not available' })}
            </Text>
          </View>
        )}

        {/* User Story 2: Company Profile (now embedded in instrument response) */}
        {profile && (
          <View style={[styles.sectionBox, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {t('portfolio.companyProfile', { defaultValue: 'Company Profile' })}
            </Text>

            {profile.ceo && (
              <DetailRow label={t('portfolio.ceo')} value={profile.ceo} theme={theme} />
            )}
            {profile.headquarters_location && (
              <DetailRow
                label={t('portfolio.headquarters')}
                value={profile.headquarters_location}
                theme={theme}
              />
            )}
            {profile.website && (
              <DetailRow label={t('portfolio.website')} value={profile.website} theme={theme} />
            )}
            {profile.employees && (
              <DetailRow
                label={t('portfolio.employees')}
                value={profile.employees.toLocaleString()}
                theme={theme}
              />
            )}
            {profile.founded_year && (
              <DetailRow
                label={t('portfolio.founded')}
                value={profile.founded_year.toString()}
                theme={theme}
              />
            )}

            {profile.description && (
              <View style={styles.descriptionSection}>
                <Text style={[styles.descriptionTitle, { color: theme.colors.text }]}>
                  {t('portfolio.about', { defaultValue: 'About' })}
                </Text>
                <Text style={[styles.description, { color: theme.colors.text }]}>
                  {profile.description}
                </Text>
              </View>
            )}

            {profile.data_freshness?.last_update && (
              <Text style={[styles.freshnessText, { color: theme.colors.textTertiary }]}>
                {t('portfolio.profileUpdated', { defaultValue: 'Profile updated' })}:{' '}
                {new Date(profile.data_freshness.last_update).toLocaleDateString()}
              </Text>
            )}
          </View>
        )}

        {/* Show profile unavailable message if no profile */}
        {!profile && (
          <View style={[styles.sectionBox, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {t('portfolio.companyProfile', { defaultValue: 'Company Profile' })}
            </Text>
            <Text style={[styles.noDataText, { color: theme.colors.textTertiary }]}>
              {t('portfolio.profileDataNotAvailable', {
                defaultValue: 'Profile data not available',
              })}
            </Text>
          </View>
        )}

        {/* Original description from instrument (fallback if no profile) */}
        {instrument?.description && !profile?.description && (
          <View style={[styles.sectionBox, { backgroundColor: theme.colors.surface }]}>
            <View style={styles.descriptionSection}>
              <Text style={[styles.descriptionTitle, { color: theme.colors.text }]}>
                {t('portfolio.about', { defaultValue: 'About' })}
              </Text>
              <Text style={[styles.description, { color: theme.colors.text }]}>
                {instrument.description}
              </Text>
            </View>
          </View>
        )}

        {/* User Story 5: Historical Price Chart */}
        {instrument && <PriceChart instrument_id={instrument.id} currency={instrument.currency} />}
      </ScrollView>
    );
  };

  return (
    <>
      {renderContent()}
      {showSelector && (
        <InstrumentSelector
          visible={showSelector}
          symbol={symbol}
          currency={currency}
          initialCandidates={resolution?.candidates || []}
          onClose={() => setShowSelector(false)}
          onSelect={handleSelectCandidate}
        />
      )}
      {showDuplicateDialog && existingPosition && (
        <DuplicateInstrumentDialog
          visible={showDuplicateDialog}
          existingPosition={existingPosition}
          newPosition={{
            symbol,
            quantity: quantity || 0,
            cost_price: cost_price,
          }}
          onAdd={() => handleDuplicateResolution('add')}
          onReplace={() => handleDuplicateResolution('replace')}
          onCancel={() => {
            setShowDuplicateDialog(false);
            setPendingInstrument(null);
            setExistingPosition(null);
          }}
        />
      )}
    </>
  );
}

function DetailRow({ label, value, theme }: { label: string; value: string; theme: any }) {
  const styles = useMemo(() => createDetailRowStyles(theme), [theme]);

  return (
    <View style={styles.detailRow}>
      <Text style={[styles.detailLabel, { color: theme.colors.textSecondary }]}>{label}:</Text>
      <Text style={[styles.detailValue, { color: theme.colors.text }]}>{value}</Text>
    </View>
  );
}

function createDetailRowStyles(theme: any) {
  return StyleSheet.create({
    detailRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderBottomColor: theme.colors.border,
    },
    detailLabel: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
    },
    detailValue: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      textAlign: 'right',
    },
  });
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    loadingText: {
      marginTop: 10,
    },
    statusBox: {
      margin: theme.spacing.md,
      padding: 20,
      borderRadius: theme.radii.md,
      borderLeftWidth: 4,
    },
    errorBox: {
      borderLeftColor: theme.colors.error,
    },
    errorTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.sm,
    },
    errorMessage: {
      fontSize: theme.typography.fontSizeSm,
    },
    unmatchedBox: {
      borderLeftColor: theme.colors.warning || '#FF9500',
    },
    ambiguousBox: {
      borderLeftColor: '#FFCC00',
    },
    statusTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
    },
    statusText: {
      fontSize: theme.typography.fontSizeMd,
      marginBottom: theme.spacing.sm,
    },
    statusDetail: {
      fontSize: theme.typography.fontSizeSm,
      marginBottom: 16,
    },
    retryButton: {
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: theme.radii.sm,
      alignItems: 'center',
    },
    retryButtonText: {
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    matchedBox: {
      margin: theme.spacing.md,
      padding: 20,
      borderRadius: theme.radii.md,
      borderLeftWidth: 4,
      borderLeftColor: theme.colors.success,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
    },
    symbol: {
      fontSize: theme.typography.fontSize3Xl,
      fontWeight: theme.typography.fontWeightBold,
    },
    badge: {
      paddingHorizontal: 12,
      paddingVertical: 4,
      borderRadius: theme.radii.full,
    },
    badgeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    companyName: {
      fontSize: theme.typography.fontSizeMd,
      marginBottom: 16,
    },
    details: {
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderTopColor: theme.colors.border,
      paddingTop: 16,
    },
    detailRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: theme.spacing.sm,
    },
    detailLabel: {
      fontSize: theme.typography.fontSizeSm,
    },
    detailValue: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
    },
    descriptionSection: {
      marginTop: 16,
      paddingTop: 16,
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderTopColor: theme.colors.border,
    },
    descriptionTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.sm,
    },
    description: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 20,
    },
    sectionBox: {
      margin: theme.spacing.md,
      marginTop: 0,
      padding: 20,
      borderRadius: theme.radii.md,
      borderLeftWidth: 4,
      borderLeftColor: theme.colors.primary,
    },
    sectionTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: 16,
    },
    sectionLoadingText: {
      marginTop: theme.spacing.sm,
      fontSize: theme.typography.fontSizeSm,
    },
    priceContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    price: {
      fontSize: 32,
      fontWeight: theme.typography.fontWeightBold,
      marginRight: 12,
    },
    priceChange: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    freshnessText: {
      marginTop: 12,
      fontSize: theme.typography.fontSizeXs,
      fontStyle: 'italic',
    },
    noDataText: {
      fontSize: theme.typography.fontSizeSm,
      fontStyle: 'italic',
    },
  });
}
